import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import _AccordionItem from './_AccordionItem.react';

/**
 * A layout component for displaying a collapsible accordion item that can be
 * used to group and organize content.
 *
 * WARNING: This is an alpha version of a component. Breaking changes may be
 * made without respecting semantic versioning. Use at your own risk.
 *
 * **Example Usage**
 * ddk._Accordion(
 *     control_groups=[
 *         {
 *             "title": "Model Parameters",
 *             "children": [
 *                 dcc.Slider(
 *                     name="n", min=0, max=10, marks={0: "0", 5: "5", 10: "10"}, value=5
 *                 ),
 *             ],
 *         },
 *         {
 *             "title": "Plot Axes",
 *             "children": [
 *                 dcc.Dropdown(
 *                     name="x-axis",
 *                     placeholder="City",
 *                     options=[
 *                         {"label": "New York City", "value": "NYC"},
 *                         {"label": "Montreal", "value": "MTL"},
 *                     ],
 *                     value="MTL",
 *                 ),
 *             ],
 *         },
 *     ]
 * )
 * ```
 */
export default function _Accordion({
    control_groups,
    action_button,
    portal_ref,
}) {
    const isOnlyOneControlGroup = control_groups.length === 1;
    // The control header text at the top of the accordion next to the button
    // that collapses the control panel. If there's only one control group, then
    // we don't want to show any accordion header, because it is essentially
    // redundant. Instead, we will display the title of the first and only
    // control group as this control header text. We don't check to see if the
    // user provider to title here, because we want to allow them to leave it
    // empty rather than defaulting to "Controls".
    //
    // The control header text is rendered in the ControlPanel component.
    // Perhaps it should actually be rendered in this component, but we would
    // need to rework the markup and styling to make that happen. Instead, at
    // least for the short term, we use a portal to run that text in the parent
    // component.
    const controlHeaderText = isOnlyOneControlGroup
        ? control_groups[0].title
        : 'Controls';
    return (
        <>
            {portal_ref && createPortal(controlHeaderText, portal_ref)}
            <div
                className={`accordion ${action_button ? 'accordion_with_action' : 'accordion_without_action'}`}
            >
                {control_groups.map(({title, description, id, children}) => (
                    <_AccordionItem
                        title={title}
                        description={description}
                        key={id}
                        isOnlyOneControlGroup={isOnlyOneControlGroup}
                    >
                        {children}
                    </_AccordionItem>
                ))}
            </div>
            {action_button && (
                <div className="action_button_container">{action_button}</div>
            )}
        </>
    );
}

_Accordion.propTypes = {
    /**
     * Each control group will make up one section of the accordion (one AccordionItem).
     * The children of that group will be placed inside that section/AccordionItem.
     */
    control_groups: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * The title of the accordion section.
             */
            title: PropTypes.string.isRequired,
            /**
             * The description of the accordion section.
             */
            description: PropTypes.string.isRequired,
            /**
             * The id of the accordion section.
             */
            id: PropTypes.string.isRequired,
            /**
             * The elements to be placed in this group accordion section/AccordionItem.
             */
            children: PropTypes.arrayOf(PropTypes.node).isRequired,
        })
    ).isRequired,
    /**
     * The action button to be placed at the bottom of the accordian.
     */
    action_button: PropTypes.node,
    // portal ref for header with one tab
    portal_ref: PropTypes.any,
};
